import type { ProgressState, ReviewReport, SSEEventData } from "@/types";

export interface ReviewStreamCompletion {
  result: ReviewReport;
  reviewId?: string;
}

export function parseReviewCompletePayload(data: SSEEventData): ReviewStreamCompletion | null {
  const actualResult = data.result || (data as unknown as ReviewReport);
  const actualStatus = data.status;

  if (actualStatus === "success" || actualStatus === "completed" || !actualStatus) {
    if (actualResult?.reviewTable && Array.isArray(actualResult.reviewTable)) {
      return {
        result: actualResult as ReviewReport,
        reviewId: data.reviewId,
      };
    }
  }
  return null;
}

/** Merge a `progress` SSE payload into UI progress state (matches useSSE handleProgress). */
export function applySseProgressEvent(prev: ProgressState, data: SSEEventData): ProgressState {
  if (data.message) {
    return {
      ...prev,
      message: data.message,
      step: data.step,
      progress: data.progress !== undefined ? data.progress : prev.progress,
      isConnected: true,
      logs: [...prev.logs, { timestamp: new Date(), message: data.message, step: data.step }],
    };
  }
  if (data.progress !== undefined) {
    return { ...prev, progress: data.progress, isConnected: true };
  }
  return prev;
}

export interface ContractReviewSseCallbacks {
  onProgress?: (data: SSEEventData) => void;
  onHeartbeat?: () => void;
}

/**
 * POST JSON and consume SSE until `complete` or `error`.
 */
export async function postContractReviewSse(
  url: string,
  requestData: Record<string, unknown>,
  accessToken: string | null | undefined,
  callbacks?: ContractReviewSseCallbacks,
): Promise<ReviewStreamCompletion | null> {
  let currentEvent = "";

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.length > 0 && !buffer.endsWith("\n") ? lines.pop() || "" : "";

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        if (trimmedLine.startsWith("event: ")) {
          currentEvent = trimmedLine.substring(7).trim();
        } else if (trimmedLine.startsWith("data: ")) {
          try {
            const dataStr = trimmedLine.substring(6);
            const data: SSEEventData = JSON.parse(dataStr);
            const eventType = currentEvent || "message";

            switch (eventType) {
              case "run-started":
                console.log("Review SSE run started:", data.runId);
                break;
              case "progress":
                callbacks?.onProgress?.(data);
                break;
              case "heartbeat":
                callbacks?.onHeartbeat?.();
                break;
              case "complete": {
                const completed = parseReviewCompletePayload(data);
                return completed;
              }
              case "error": {
                const msg =
                  typeof (data as { error?: string }).error === "string"
                    ? (data as { error: string }).error
                    : typeof (data as { message?: string }).message === "string"
                      ? (data as { message: string }).message
                      : "Review failed";
                throw new Error(msg);
              }
              case "message":
                if (data.status === "success" && data.result) {
                  return parseReviewCompletePayload(data);
                }
                break;
            }
          } catch (parseErr) {
            console.error("Failed to parse SSE data:", trimmedLine, parseErr);
          }
        }
      }
    }

    return null;
  } catch (err) {
    console.error("Contract review SSE error:", err);
    if (err instanceof Error) throw err;
    return null;
  }
}
